import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {m} from 'framer-motion';
// @mui
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
// hooks
import {useSession} from 'packages/common/hooks/session';
import {useAdminBannerOffset} from 'packages/common/hooks/useAdminBannerOffset';
import {usePathname} from 'packages/common/routes/hooks';
// components
import {varHover} from 'packages/common/components/animate';
import {usePopover} from 'packages/common/components/custom-popover';
import SvgColor from 'packages/common/components/svg-color';
import Logo from 'packages/common/components/logo';
import CompanySwitcher from 'packages/common/components/company-switcher/CompanySwitcher';
import UserAccountPopover from './user-account-popover';
// config
import {SAFE_AREA_X} from '../config-layout';
import {useNavData} from './config-navigation';

/**
 * Mini header mobile pour le layout app
 *
 * Affiche uniquement sur mobile avec le bouton hamburger et l'avatar utilisateur
 * La hauteur est optimisée pour maximiser l'espace vertical disponible sur mobile et tablette
 *
 * @module MobileHeader
 * @author Équipe Brydge
 * @since 2.1.0
 */

const MOBILE_HEADER_HEIGHT = 48;

export default function MobileHeader({onOpenNav}) {
  const {user} = useSession();
  const adminBannerOffset = useAdminBannerOffset();
  const popover = usePopover();
  const pathname = usePathname();
  const navData = useNavData();

  /**
   * Récupère le titre de la page courante à partir de la configuration de navigation
   * Effectue une recherche récursive et utilise un fallback sur le parent si la page exacte n'est pas trouvée
   */
  const pageTitle = useMemo(() => {
    let exactMatch = null;
    let parentMatch = null;

    const searchItems = (items) => {
      for (const item of items) {
        if (item.path === pathname) {
          exactMatch = item.title;
          return;
        }
        if (item.path && pathname.startsWith(item.path) && item.path.length > (parentMatch?.length || 0)) {
          parentMatch = {title: item.title, length: item.path.length};
        }
        if (item.children) {
          searchItems(item.children);
        }
      }
    };

    for (const group of navData) {
      if (group.items) {
        searchItems(group.items);
      }
    }

    return exactMatch || parentMatch?.title || '';
  }, [pathname, navData]);

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          'display': {lg: 'none'},
          'top': `calc(${adminBannerOffset}px + env(safe-area-inset-top, 0px))`,
          'height': MOBILE_HEADER_HEIGHT,
          // Le fond couvre toute la largeur, seul le contenu se retire de l'encoche
          'pl': SAFE_AREA_X.LEFT,
          'pr': SAFE_AREA_X.RIGHT,
          'zIndex': (theme) => theme.zIndex.appBar,
          'bgcolor': (theme) => theme.palette.navy[900],
          'boxShadow': '0 2px 8px 0 rgba(15, 28, 46, 0.3)',
          // Remplit la zone de la barre d'etat iOS quand il n'y a pas de banniere admin
          ...(!adminBannerOffset && {
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 'calc(-1 * env(safe-area-inset-top, 0px))',
              left: 0,
              right: 0,
              height: 'env(safe-area-inset-top, 0px)',
              bgcolor: 'inherit',
            },
          }),
        }}
      >
        <Toolbar
          sx={{
            height: MOBILE_HEADER_HEIGHT,
            px: 2,
            py: 0,
            minHeight: `${MOBILE_HEADER_HEIGHT}px !important`,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* Bouton hamburger */}
          <IconButton
            onClick={onOpenNav}
            edge="start"
            sx={{
              color: 'white',
              p: 0.75,
              mr: 1,
            }}
          >
            <SvgColor src="/assets/icons/navbar/ic_menu_item.svg" sx={{width: 24, height: 24}} />
          </IconButton>

          {/* Logo - lien direct vers le dashboard */}
          <Logo variant="white" isSmall sx={{width: 28, height: 28, mr: 1.5}} />

          {/* Titre de la page courante */}
          <Typography
            variant="subtitle1"
            noWrap
            sx={{
              color: 'white',
              fontWeight: 600,
              flex: 1,
              minWidth: 0,
            }}
          >
            {pageTitle}
          </Typography>

          {/* Company Switcher mini */}
          <CompanySwitcher variant="mini" sx={{mr: 1.5}} />

          {/* Avatar utilisateur */}
          <IconButton
            component={m.button}
            whileTap="tap"
            whileHover="hover"
            variants={varHover(1.05)}
            onClick={popover.onOpen}
            sx={{
              width: 32,
              height: 32,
              p: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Avatar
              src={user?.photoURL}
              alt={`${user?.firstName} ${user?.lastName}`}
              sx={{
                width: 32,
                height: 32,
                border: 'solid 2px rgba(255, 255, 255, 0.3)',
                bgcolor: (theme) => theme.palette.primary.main,
                color: 'white',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {user?.firstName?.charAt(0)?.toUpperCase() || ''}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Popover utilisateur réutilisable */}
      <UserAccountPopover
        popover={popover}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
      />
    </>
  );
}

MobileHeader.propTypes = {
  onOpenNav: PropTypes.func,
};

export {MOBILE_HEADER_HEIGHT};
